import { colors as themeColors } from '../../utils/theme';
import { colors, gradients } from '../../utils/theme';
import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, useTheme, useMediaQuery, Card, CardContent, Divider, CircularProgress, TextField, IconButton, Tooltip, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import NoDataComponent from "../../components/NoDataComponent";
import CustomNoDataOverlay from "../../components/CustomNoDataOverlay";
import { useAuth } from "../../context/AuthContext";
import { VscNewFile } from "react-icons/vsc";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import SkeletonLoader from "../../components/SkeletonLoader";
import LoadingOverlay from "../../components/LoadingOverlay";
import { motion } from "framer-motion";

// FORMAT INR
const formatINR = (val) =>
    Number(val || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

export default function VoucherDetails({ connectionString }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isSmall = useMediaQuery(theme.breakpoints.down(500));
    const navigate = useNavigate();

    const { selectedCompany } = useAuth();

    const [rows, setRows] = useState([]);
    // PDF column: fetches the voucher's PDF data (bankcashreceiptmaster + jvdts + billwiseentry)
    // and logs it; PDF rendering will be wired on top of this data later.
    const [loadingPdfId, setLoadingPdfId] = useState(null);
    const handlePdfClick = async (event, row) => {
        event.stopPropagation();
        if (loadingPdfId) return;
        // Opened first (synchronously) so the browser doesn't block the popup; the A5 print page loads its own data.
        window.open(`/voucher/${row.id}?companyId=${encodeURIComponent(companyId)}`, '_blank', 'noopener,noreferrer');
        setLoadingPdfId(row.id);
        try {
            const res = await axios.get(`${API_URL}/get-voucher-pdf-data`, {
                params: { receiptId: row.id, companyId }
            });
            console.log("Voucher PDF data:", JSON.stringify(res.data, null, 2));
        } catch (err) {
            console.error("Voucher PDF data fetch failed:", err?.response?.data || err);
        } finally {
            setLoadingPdfId(null);
        }
    };
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");

    const companyId = selectedCompany?.CompId;
    const API_URL = `${connectionString}/api`;
    const [selectionModel, setSelectionModel] = useState([]);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [deleting, setDeleting] = useState(false);

    // =========================
    // FETCH VOUCHER LIST
    // =========================
    const fetchVouchers = async () => {
        try {
            setLoading(true);

            const res = await axios.get(`${API_URL}/get-voucher-list`, {
                params: { companyId }
            });

            const data = res.data?.data || [];

            const mapped = data.map((row) => ({
                id: row.id,
                billDate: row.ReceiptDate
                    ? new Date(row.ReceiptDate).toLocaleDateString("en-GB")
                    : "",
                voucherNo: row.ReceiptNumber,
                refNo: row.RefNo,
                voucherTotal: row.VoucherTotal,
                remark: row.Remark,
            }));

            setRows(mapped);
        } catch (err) {
            console.error("Voucher Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (companyId) fetchVouchers();
    }, [companyId]);

    // =========================
    // SEARCH FILTER
    // =========================
    const filteredRows = rows.filter((row) =>
        [row.voucherNo, row.refNo, row.remark]
            .join(" ")
            .toLowerCase()
            .includes(searchText.toLowerCase())
    );


    const handleDelete = (row) => {
        const ids =
            selectionModel.length > 0
                ? selectionModel
                : row
                    ? [row.id]
                    : [];

        if (!ids.length) return;

        setPendingDeleteIds(ids);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (deleting) return;
        setDeleting(true);
        try {
            // Delete all selected ids in a single request/transaction
            await axios.post(`${API_URL}/delete-voucher`, {
                receiptIds: pendingDeleteIds,
                CompanyId: companyId,
            });

            setSelectionModel([]);

            // Re-fetch vouchers to ensure UI matches server state
            await fetchVouchers();
        } catch (err) {
            console.error("Delete Voucher Error:", err);
            // Optionally show a snackbar/alert here
        } finally {
            setDeleting(false);
            setOpenDeleteDialog(false);
            setPendingDeleteIds([]);
        }
    };


    const handleSelectAllVisible = () => {
        const visibleIds = filteredRows.map(row => row.id);

        const allSelected = visibleIds.every(id =>
            selectionModel.includes(id)
        );

        if (allSelected) {
            setSelectionModel(prev =>
                prev.filter(id => !visibleIds.includes(id))
            );
        } else {
            setSelectionModel(prev => [
                ...new Set([...prev, ...visibleIds])
            ]);
        }
    };

    const allVisibleSelected =
        filteredRows.length > 0 &&
        filteredRows.every(row => selectionModel.includes(row.id));

    // =========================
    // DATAGRID COLUMNS
    // =========================
    const columns = [
        { field: "billDate", headerName: "Bill Date", flex: 1 },

        { field: "voucherNo", headerName: "Voucher No", flex: 1.5,
            renderCell: ({ value, row }) => (
                <Typography sx={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} onClick={() => navigate(`/main/voucher-edit/${row.id}`)} >
                    {value}
                </Typography>
            ),
        },

        { field: "refNo", headerName: "Ref No", flex: 1.2 },

        { field: "voucherTotal", headerName: "Amount", flex: 1, valueGetter: (value) => (value === null || value === undefined ? "" : String(value)), renderCell: ({ value }) => formatINR(value), },

        { field: "remark", headerName: "Remark", flex: 2 },

        // PDF COLUMN
        {
            field: "pdf", headerName: "Action", flex: 0.4, sortable: false, filterable: false, align: "center", headerAlign: "center",
            renderCell: (params) => {
                const thisId = params.row.id;
                if (loadingPdfId && Number(loadingPdfId) === Number(thisId)) {
                    return <CircularProgress size={20} />;
                }
                return (
                    <Tooltip title="PDF">
                        <IconButton color="primary" onClick={(e) => { handlePdfClick(e, params.row); }}>
                            <PrintIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        },

        // ✅ DELETE COLUMN
        { field: "delete", headerName: "", flex: 0.5, sortable: false, filterable: false, align: "center",
            renderCell: (params) => {
                const isSelected = selectionModel.includes(params.row.id);

                return (
                    <Tooltip title={isSelected ? "Delete Selected" : "Select row to enable"}>
                        <span>
                            <IconButton color="error" disabled={!isSelected}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(params.row);
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        },
    ];

    // =========================
    // MOBILE CARD
    // =========================
    const MobileCard = ({ row }) => {
        const isSelected = selectionModel.includes(row.id);

        const toggleSelection = (e) => {
            e.stopPropagation();
            setSelectionModel(prev =>
                prev.includes(row.id)
                    ? prev.filter(id => id !== row.id)
                    : [...prev, row.id]
            );
        };

        return (
            <Card
                onClick={() => navigate(`/main/voucher-edit/${row.id}`)}
                sx={{ mb: 1.8, borderLeft: `5px solid ${colors.primary.main}`, borderRadius: 2, boxShadow: 2, cursor: 'pointer',
                    '&:hover': { boxShadow: 4 }
                }}
            >
                <CardContent sx={{ py: 1.5 }}>

                    {/* 🔥 TOP ROW (EXACT LIKE PRODUCTION) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>

                        {/* CHECKBOX */}
                        <Checkbox checked={isSelected} onChange={toggleSelection} onClick={(e) => e.stopPropagation()} size="small" />

                        {/* VOUCHER INFO */}
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                {row.voucherNo}
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "#777" }}>
                                {row.billDate}
                            </Typography>
                        </Box>
                    </Box>

                    {/* DETAILS */}
                    <Box sx={{ mt: 1 }}>
                        <Typography sx={{ fontSize: "0.8rem" }}>
                            <strong>Ref No:</strong> {row.refNo || "-"}
                        </Typography>

                        <Typography sx={{ fontSize: "0.8rem", mt: 0.5 }}>
                            <strong>Amount:</strong> ₹{formatINR(row.voucherTotal)}
                        </Typography>

                        <Typography sx={{ fontSize: "0.8rem", mt: 0.5 }}>
                            <strong>Remark:</strong> {row.remark || "-"}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    // =========================
    // UI
    // =========================
    return (
        <Box sx={{ pt: 1, px: { xs: 2, sm: 3, md: 4 } }}>

            {/* HEADER */}

            <Box sx={{ position: "relative", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
                <Box sx={{ position: "absolute", left: 0 }}>
                    <Tooltip title="Add New Voucher">
                        <IconButton component={Link} to="/main/Vouchers" sx={{ color: "orange" }}>
                            <VscNewFile size={25} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography variant="h5" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" }, fontWeight: 600, textAlign: "center", }}>
                    {isSmall ? "Voucher" : "Voucher Detail"}
                </Typography>
            </Box>

            <Paper elevation={3} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 0.5 }}>

                    {/* MOBILE */}
                    {isMobile ? (
                        <>
                            <TextField fullWidth size="small" placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} sx={{ mb: 1.5 }} />

                            {selectionModel.length > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2, py: 1.2, borderRadius: 2, bgcolor: themeColors.neutral.surface, boxShadow: 1 }} >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox checked={allVisibleSelected} onChange={handleSelectAllVisible} size="small" />
                                        <Typography sx={{ fontSize: "0.85rem", ml: 1 }}>
                                            {selectionModel.length} selected
                                        </Typography>
                                    </Box>

                                    <IconButton
                                        color="error"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete();
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            )}

                            {loading && <SkeletonLoader count={3} height={120} />}
                            {!loading && rows.length === 0 && <NoDataComponent />}
                            {!loading && filteredRows.length === 0 && <CustomNoDataOverlay />}
                            {!loading && filteredRows.length > 0 && filteredRows.map((row) => (
                                <MobileCard key={row.id} row={row} />
                            ))}
                        </>
                    ) : (
                        <Box sx={{ height: "78vh", width: "100%", position: 'relative' }}>
                            <LoadingOverlay isVisible={loading} />
                            {!loading && rows.length === 0 && <NoDataComponent />}
                            {!loading && rows.length > 0 && <DataGrid
                                    rows={filteredRows}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    checkboxSelection
                                    rowSelectionModel={selectionModel}
                                    onRowSelectionModelChange={(newSel) =>
                                        setSelectionModel(newSel.map(Number))
                                    }
                                    pageSizeOptions={[50, 100]}
                                    initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                                    sx={{
                                        '& .MuiDataGrid-columnHeaders': { backgroundColor: themeColors.neutral.gridHeader, borderBottom: `1px solid ${themeColors.neutral.border}` },
                                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                        '& .MuiDataGrid-columnHeader': { backgroundColor: themeColors.neutral.gridHeader, color: themeColors.neutral.ink, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
                                        '& .MuiDataGrid-filler': { backgroundColor: themeColors.neutral.surface },
                                        '& .MuiDataGrid-scrollbarFiller': { backgroundColor: themeColors.neutral.gridHeader },
                                        '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface },
                                        '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.neutral.surfaceAlt },
                                        '& .MuiDataGrid-row:hover': { backgroundColor: themeColors.primary.tint },
                                        '& .MuiDataGrid-row.Mui-selected': { backgroundColor: `${themeColors.primary.selected} !important` },
                                        '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                    }}
                                    slots={{
                                        noResultsOverlay: CustomNoDataOverlay,
                                    }}
                                />}
                        </Box>
                    )}
                </Box>

                <Dialog open={openDeleteDialog} onClose={() => { if (!deleting) setOpenDeleteDialog(false); }} maxWidth="xs" fullWidth>
                    {deleting
                        ? <LinearProgress color="error" sx={{ height: 4 }} />
                        : <Box sx={{ height: 4, background: gradients.danger }} />}
                    <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>Confirm Deletion</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2.5, py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Are you sure you want to delete{' '}
                            <Typography component="span" variant="body2" fontWeight={700} color="error.main">{pendingDeleteIds.length}</Typography>
                            {' '}record(s)? This action cannot be undone.
                        </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined" color="inherit" size="small" disabled={deleting}>Cancel</Button>
                        <Button color="error" variant="contained" size="small" disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                            onClick={confirmDelete}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                    </Box>
                </Dialog>
            </Paper>
        </Box>
    );
}